import { BackLink, Button, Intro } from "components/commons";
import { FormProvider } from "contexts/form-context";
import { FormMode, Path, OrderStatus } from "enums";
import locale from "localization/en";
import { useCallback, useMemo, useEffect, useState } from "react";
import initialState from "./order-summary-form.state";
import OrderSummaryForm from "./order-summary-form/order-summary-form";
import ViewOrderSummary from "./view-order-summary";
import { getOrderSummaryByIdApi, updateOrderSummaryByIdApi } from "apis";
import styles from "./order-summary-form/order-summary.module.scss";
import { useApi, useModal, useMount, useRouter } from "hooks";
import { EditRounded } from "@material-ui/icons";
import OrderSummaryModal from "./order-summary-modal";
import { stringToDecimal } from "utils/text.utils";

const OrderSummaryDetailsModule = () => {
  const router = useRouter();
  const formMode = router.query?.mode || router.query?.pageMode || FormMode.Add;
  const isEdit = formMode === FormMode.Edit;
  const isView = formMode === FormMode.View;
  const orderId = router?.query?.id;

  const [form, setForm] = useState(null);

  const confirmModal = useModal();
  const [modalType, setModalType] = useState(null);
  const [modalItems, setModalItems] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openModal = (type, items) => {
    setModalType(type);
    setModalItems(items);
    confirmModal.show();
  };

  const { request: updateOrderSummary, loading: isUpdating } = useApi({
    api: updateOrderSummaryByIdApi,
    modalError: true,
  });

  const { request: getOrderSummaryByOrderId, result: orderSummary } = useApi({
    api: getOrderSummaryByIdApi,
  });

  useEffect(() => {
    if (!confirmModal.isOpen && modalType?.includes("success")) {
      getOrderSummaryByOrderId({ id: orderId }, { useLoading: true });
    }
    if (isEdit && orderSummary?.status) {
      const isAllowed =
        orderSummary?.status === OrderStatus.ForRevision ||
        orderSummary?.status === OrderStatus.Processing;

      if (!isAllowed) {
        router.push(Path.ViewOrderSummaryDetails(orderId, FormMode.View));
      }
    }
  }, [
    confirmModal.isOpen,
    modalType,
    isEdit,
    orderSummary?.status,
    orderId,
    getOrderSummaryByOrderId,
    router,
  ]);

  const handleOnSubmit = useCallback(
    async (fields, error) => {
      let payload = {};
      confirmModal.close();
      if (!error) {
        switch (modalType) {
          case "confirm-edit":
            const formattedOrderItems = fields.orderItems.value.map((item) => ({
              ...item,
              amount: stringToDecimal(item.amount),
            }));
            payload = {
              status: OrderStatus.ForApproval,
              purchaseOrderNumber: fields.purchaseOrderNumber.value,
              salesOrderNumber: fields.salesOrderNumber.value,
              remarksHo: fields.remarksHo.value,
              orderItems: formattedOrderItems,
            };
            break;

          case "confirm-approval":
            payload = {
              status: OrderStatus.ForDelivery,
            };
            break;
          case "confirm-reject":
            payload = {
              status: OrderStatus.Rejected,
              reason: fields.reason.value ?? "",
              reasonRemarks: fields.reasonRemarks.value ?? "",
            };

            break;
          case "confirm-revision":
            payload = {
              status: OrderStatus.ForRevision,
              reason: fields.reason.value,
              reasonRemarks: fields.reasonRemarks.value ?? "",
            };
            break;
          default:
            break;
        }
      }

      if (!error) {
        const res = await updateOrderSummary(
          { id: orderId, ...payload },
          () => handleOnSubmit(fields, error),
          { reqError: true },
        );
        if (res?.error) return;

        switch (modalType) {
          case "confirm-approval":
            openModal("success-approval", []);
            break;
          case "confirm-reject":
            openModal("success-reject", []);
            break;
          case "confirm-revision":
            openModal("success-revision", []);
            break;
          case "confirm-edit":
            openModal("success-edit", []);
            break;
          default:
            break;
        }
      }
    },
    [confirmModal, modalType, updateOrderSummary, orderId, openModal],
  );

  useMount(async () => {
    await getOrderSummaryByOrderId({ id: orderId }, { useLoading: true });
  });

  return (
    <>
      <BackLink text={locale.orderSummary} path={Path.OrderSummary} />
      <FormProvider
        initialState={useMemo(() => initialState(orderSummary), [orderSummary])}
        onChange={setForm}
        onSubmit={handleOnSubmit}
      >
        <Intro
          title={`${locale.orderId}: ${orderId || "--"}`}
          actionList={
            <>
              {orderSummary?.order?.status === OrderStatus.ForApproval && (
                <div className={styles.buttonContainer}>
                  <Button primary onClick={() => openModal("confirm-approval", [])}>
                    {locale.approve}
                  </Button>
                  <Button
                    primary
                    onClick={() =>
                      openModal("confirm-reject", [
                        <>
                          {locale.orderId}: {orderId}
                        </>,
                      ])
                    }
                  >
                    {locale.reject}
                  </Button>
                  <Button
                    primary
                    onClick={() =>
                      openModal("confirm-revision", [
                        <>
                          {locale.orderId}: {orderId}
                        </>,
                      ])
                    }
                  >
                    {locale.requestRevision}
                  </Button>
                </div>
              )}

              {orderSummary?.order?.status === OrderStatus.Processing && (
                <div className={styles.buttonContainer}>
                  <Button
                    primary
                    startIcon={isView ? <EditRounded /> : null}
                    disabled={isView ? false : !form?.isFormSubmittable}
                    onClick={() => {
                      if (isView) {
                        router.push(Path.ViewOrderSummaryDetails(orderId, FormMode.Edit));
                        return;
                      }
                      if (!form) return;
                      if (
                        form.error &&
                        Object.keys(form.error).length > 0 &&
                        !form?.isFormSubmittable
                      )
                        return;
                      openModal("confirm-edit", []);
                    }}
                  >
                    {isView ? locale.edit : locale.saveChanges}
                  </Button>
                </div>
              )}
            </>
          }
        />

        <br />

        {isView ? (
          <ViewOrderSummary orderSummary={orderSummary?.order} loading={!orderSummary} />
        ) : (
          <OrderSummaryForm loading={isUpdating || !orderSummary} orderSummary={orderSummary} />
        )}

        <OrderSummaryModal
          modal={confirmModal}
          type={modalType}
          items={modalItems}
          onPrimaryClick={handleOnSubmit}
          router={router}
        />
      </FormProvider>
    </>
  );
};

export default OrderSummaryDetailsModule;
